const INVALID_RESPONSE_MESSAGE = "Format respons akses transfer balik tidak valid";

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Extracts one page of POS users and their return-transfer access from the
 * `GET /api/qrs/return-access` response body.
 * @param {unknown} body Response body with `data` rows and pagination `meta`.
 * @returns {{
 *   rows: { userId: number, posCode: string, posName: string, dealerName: string }[],
 *   total: number,
 *   lastPage: number,
 *   totalPosUsers: number,
 *   allowedUserIds: number[],
 * }} Parsed page; `allowedUserIds` covers every page, not only this one.
 */
export function parseReturnAccessPage(body) {
  const data = body ? body.data : undefined;
  const meta = body ? body.meta : undefined;
  if (
    !Array.isArray(data) ||
    !meta ||
    !isFiniteNumber(meta.total) ||
    !isFiniteNumber(meta.lastPage) ||
    !isFiniteNumber(meta.totalPosUsers) ||
    !Array.isArray(meta.allowedUserIds)
  ) {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }

  const rows = data.map((item) => {
    if (!item || !isFiniteNumber(item.user_id)) {
      throw new Error(INVALID_RESPONSE_MESSAGE);
    }
    return {
      userId: item.user_id,
      posCode: item.pos_code || "",
      posName: item.pos_name || "",
      dealerName: item.dealer_name || "",
    };
  });

  return {
    rows,
    total: meta.total,
    lastPage: meta.lastPage,
    totalPosUsers: meta.totalPosUsers,
    allowedUserIds: meta.allowedUserIds.map(Number).filter(Number.isInteger),
  };
}

/**
 * Fetches one page of POS users for the Atur Akses Transfer Balik setting.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @param {{ page: number, limit: number, search?: string }} query Page query.
 * @returns {Promise<ReturnType<typeof parseReturnAccessPage>>}
 */
export async function fetchReturnAccessPage(http, { page, limit, search }) {
  const params = { page, limit, search: search || undefined };
  const resp = await http.get("/api/qrs/return-access", { params });
  return parseReturnAccessPage(resp.data);
}

const ALL_IDS_PAGE_LIMIT = 100;

/**
 * Collects the user IDs of every POS user matching `search`, across all pages,
 * so a select-all can cover pages that are not loaded.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @param {{ search?: string }} query Search applied to the user list.
 * @returns {Promise<number[]>}
 */
export async function fetchAllReturnAccessIds(http, { search }) {
  const ids = [];
  for (let page = 1; ; page += 1) {
    const result = await fetchReturnAccessPage(http, {
      page,
      limit: ALL_IDS_PAGE_LIMIT,
      search,
    });
    result.rows.forEach((row) => ids.push(row.userId));
    if (result.rows.length === 0 || ids.length >= result.total) {
      return Array.from(new Set(ids));
    }
  }
}

/**
 * Extracts whether the logged-in POS may return QR to its parent dealer from
 * the `GET /api/qrs/return/parent` response body.
 * @param {unknown} body Response body whose `data` holds `can_return`.
 * @returns {boolean} `true` only when the backend explicitly allows returns.
 */
export function parseOwnReturnAccess(body) {
  const data = body ? body.data : undefined;
  if (!data || typeof data.can_return !== "boolean") {
    throw new Error(INVALID_RESPONSE_MESSAGE);
  }
  return data.can_return;
}

/**
 * Fetches the logged-in POS user's own return-transfer access.
 * @param {import("axios").AxiosInstance} http Configured axios instance.
 * @returns {Promise<boolean>}
 */
export async function fetchOwnReturnAccess(http) {
  const resp = await http.get("/api/qrs/return/parent");
  return parseOwnReturnAccess(resp.data);
}
